import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import curebay from '../../assets/images/CBLogo.png';
import apiService from '../../services/apiservice';
import PropTypes from 'prop-types';

const ACTION_TYPES = {
  APPROVE: 'A',
  REJECT: 'R'
};

const MAX_CHARACTERS = 500;

// Extracted LoadingSpinner component outside of parent component
const LoadingSpinner = () => (
  <svg 
    className="animate-spin h-5 w-5 text-white" 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24"
    aria-label="Loading"
  >
    <circle 
      className="opacity-25" 
      cx="12" 
      cy="12" 
      r="10" 
      stroke="currentColor" 
      strokeWidth="4" 
    />
    <path 
      className="opacity-75" 
      fill="currentColor" 
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
    />
  </svg>
);

// Extracted form components to reduce nesting
const ApprovalForm = ({ 
  isSubmitting, 
  approvalReason, 
  onApprovalReasonChange, 
  onSubmit, 
  onCancel 
}) => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-6">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
        <span className="text-white font-bold">✓</span>
      </div>
      <h3 className="text-lg font-semibold text-green-700">Add Approval Details (Optional)</h3>
    </div>
    
    <p className="text-sm text-green-600 mb-4">
      You are about to approve this trauma case. Add any additional comments or notes (optional).
    </p>
    
    <div className="mb-4">
      <label htmlFor="approvalNotes" className="block text-sm font-medium text-gray-700 mb-2">
        Approval Notes
      </label>
      <textarea
        id="approvalNotes"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition duration-200"
        rows="4"
        placeholder="Add any notes about this approval (optional)..."
        value={approvalReason}
        onChange={onApprovalReasonChange}
        maxLength={MAX_CHARACTERS}
        aria-describedby="approvalNotesCounter"
      />
      <div id="approvalNotesCounter" className="text-right text-sm text-gray-500 mt-1">
        {approvalReason?.length || 0}/{MAX_CHARACTERS} characters
      </div>
    </div>
    
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <button 
        className="btn bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg w-full sm:w-auto transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={onSubmit}
        disabled={isSubmitting}
        aria-label="Submit approval"
      >
{isSubmitting ? (
  <>
    <LoadingSpinner />
    {" "}Processing...
  </>
) : (
  "Submit Approval"
)}

      </button>
      <button 
        className="btn bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-6 rounded-lg w-full sm:w-auto transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={onCancel}
        disabled={isSubmitting}
        aria-label="Cancel approval"
      >
        Cancel
      </button>
    </div>
  </div>
);

ApprovalForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  approvalReason: PropTypes.string,
  onApprovalReasonChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const RejectionForm = ({ 
  isSubmitting, 
  rejectionReason, 
  onRejectionReasonChange, 
  onSubmit, 
  onCancel 
}) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-6">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
        <span className="text-white font-bold">✗</span>
      </div>
      <h3 className="text-lg font-semibold text-red-700">Add Rejection Details (Optional)</h3>
    </div>
    
    <p className="text-sm text-red-600 mb-4">
      You are about to reject this trauma case. Add a reason for rejection (optional).
    </p>
    
    <div className="mb-4">
      <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
        Rejection Reason
      </label>
      <textarea
        id="rejectionReason"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
        rows="4"
        placeholder="Add a reason for rejection (optional)..."
        value={rejectionReason}
        onChange={onRejectionReasonChange}
        maxLength={MAX_CHARACTERS}
        aria-describedby="rejectionReasonCounter"
      />
      <div id="rejectionReasonCounter" className="text-right text-sm text-gray-500 mt-1">
        {rejectionReason?.length || 0}/{MAX_CHARACTERS} characters
      </div>
    </div>
    
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <button 
        className="btn bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-lg w-full sm:w-auto transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={onSubmit}
        disabled={isSubmitting}
        aria-label="Submit rejection"
      >
 {isSubmitting ? (
  <>
    <LoadingSpinner />
    {" "}Processing...
  </>
) : (
  "Submit Approval"
)}

      </button>
      <button 
        className="btn bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-6 rounded-lg w-full sm:w-auto transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={onCancel}
        disabled={isSubmitting}
        aria-label="Cancel rejection"
      >
        Cancel
      </button>
    </div>
  </div>
);

RejectionForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  rejectionReason: PropTypes.string,
  onRejectionReasonChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const TraumaApproval = () => {
  const location = useLocation();
  const [approvalMessage, setApprovalMessage] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');

  const queryParams = new URLSearchParams(location.search);

  const enqId = queryParams.get('Id');
  const patientName = queryParams.get('patientName')?.replaceAll('_', ' ') || '';
  const surgeryType = queryParams.get('surgeryType') || '';
  const city = queryParams.get('city') || '';    
  const patientId = queryParams.get('patientId') || '';
  const surgeryDescription = queryParams.get('surgeryDescription')?.replaceAll('_', ' ') || '';

  const getApproval = useCallback(async (action, reason) => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        id: enqId,
        publishStatus: action,
        reason: reason || ''
      };

      const response = await apiService.traumaApproval(payload);
      setApprovalMessage(response.data.successObject?.message || 'Action completed successfully');
      setShowApprovalForm(false);
      setShowRejectionForm(false);
      setApprovalReason('');
      setRejectionReason('');
    } catch (error) {
      console.error('Approval error:', error);
      setApprovalMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [enqId]);

  const handleActionClick = useCallback((action) => {
    setSelectedAction(action);
    if (action === ACTION_TYPES.APPROVE) {
      setShowApprovalForm(true);
      setShowRejectionForm(false);
    } else {
      setShowRejectionForm(true);
      setShowApprovalForm(false);
    }
  }, []);

  const handleCancel = useCallback(() => {
    setShowApprovalForm(false);
    setShowRejectionForm(false);
    setApprovalReason('');
    setRejectionReason('');
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedAction === ACTION_TYPES.APPROVE) {
      getApproval(ACTION_TYPES.APPROVE, approvalReason);
    } else {
      getApproval(ACTION_TYPES.REJECT, rejectionReason);
    }
  }, [selectedAction, approvalReason, rejectionReason, getApproval]);

  const handleApprovalReasonChange = useCallback((e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setApprovalReason(value);
    }
  }, []);

  const handleRejectionReasonChange = useCallback((e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setRejectionReason(value);
    }
  }, []);

  const renderPatientDetails = () => (
    <div className="bg-gray-50 rounded-lg p-5 mb-6">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">Patient Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-500">Patient ID</p>
          <p className="font-medium">{patientId}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Patient Name</p>
          <p className="font-medium">{patientName}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">City</p>
          <p className="font-medium">{city}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Trauma Case</p>
          <p className="font-medium text-red-600">Yes</p>
        </div>
        <div className="md:col-span-2">
          <p className="text-sm text-gray-500">Surgery Type</p>
          <p className="font-medium">
            {surgeryType}
            {surgeryDescription && surgeryDescription !== 'null' && ` (${surgeryDescription})`}
          </p>
        </div>
      </div>
    </div>
  );

  const renderActionButtons = () => (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <button 
        className="btn bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg w-full sm:w-auto transition duration-200"
        onClick={() => handleActionClick(ACTION_TYPES.APPROVE)}
        aria-label="Approve trauma case"
      >
        Approve
      </button>
      <button 
        className="btn bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-lg w-full sm:w-auto transition duration-200"
        onClick={() => handleActionClick(ACTION_TYPES.REJECT)}
        aria-label="Reject trauma case"
      >
        Reject
      </button>
    </div>
  );

  if (approvalMessage) {
    const isApproved = approvalMessage.toLowerCase().includes('approved');
    
    return (
      <div className="flex justify-center items-center w-full min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4">
          <div className="mb-6 flex justify-center">
            <img src={curebay} alt="Curebay" className="h-10" />
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold mb-4 ${isApproved ? 'text-green-600' : 'text-red-600'}`}>
              {approvalMessage}
            </div>
            <p className="text-gray-600">
              You can close this window now.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center w-full min-h-screen bg-gray-50 p-4">
      <div className="container max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <div className="mb-6 flex justify-center md:justify-start">
            <img src={curebay} alt="Curebay" className="h-10" />
          </div>
          
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Trauma Case Approval Required</h1>
            <p className="text-gray-600 mb-4">Hi,</p>
            <p className="text-gray-600 mb-6">
              Surgery enquiry raised for trauma case. Please check the details below and acknowledge.
            </p>
            
            {renderPatientDetails()}
          </div>

          {!showApprovalForm && !showRejectionForm && renderActionButtons()}
          
          {showApprovalForm && (
            <ApprovalForm
              isSubmitting={isSubmitting}
              approvalReason={approvalReason}
              onApprovalReasonChange={handleApprovalReasonChange}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
            />
          )}
          
          {showRejectionForm && (
            <RejectionForm
              isSubmitting={isSubmitting}
              rejectionReason={rejectionReason}
              onRejectionReasonChange={handleRejectionReasonChange}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
            />
          )}
          
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-gray-600">
              Thanks,<br />
              <span className="font-semibold">CureBay Support</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraumaApproval;